import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from 'src/app/core/api/api.service';
import { endpoints } from 'src/app/core/config/endpoints';
import { TicketNote } from 'src/app/core/models/appointment-note.model';
import { Appointment } from 'src/app/core/models/appointment.model';
import { AttachmentFile, AttachmentUploadResponse } from 'src/app/core/models/attachment.model';
import { Charge } from 'src/app/core/models/charge.model';
import { StatusPatchData } from 'src/app/core/models/status-patch-data.model';
import { AppointmentClearCode, ClearAppointment } from 'src/app/core/models/ticket-clear-code-type.model';
import { DownloadFileService } from 'src/app/core/services/downloadFile/download-file.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { FieldEntry, WorkforceFormEntry } from 'src/app/core/models/workforce-form.model';
import { applyPathTemplate } from '@angular-devkit/schematics';

@Injectable({
     providedIn: 'root'
})
export class AppointmentService {
     activeAppointment$ = new BehaviorSubject<Appointment>(null);

     constructor(
          private apiService: ApiService,
          private storage: LocalStorageService,
          private downloadFileService: DownloadFileService
     ) { }

     async getAllAppointments(): Promise<Appointment[]> {
          const appointments = await this.storage.appointments.getAll();
          appointments.sort((b, a) => new Date(b.apptStart).getTime() - new Date(a.apptStart).getTime());

          return appointments;
     }

     setActiveAppointment(appointment: Appointment): void {
          this.activeAppointment$.next(appointment);
     }

     updateStatus<T>(patchData: StatusPatchData): Observable<T> {
          const url = endpoints.appointment.updateStatus.replace(':id', `${patchData.id}`);

          return this.apiService.patch<T>(url, patchData);
     }

     addTicketNote(ticketNote: TicketNote): void {
          const url = endpoints.appointment.ticketnotes;
          this.apiService.post<TicketNote>(url, ticketNote);
     }

     async clearAppointment(postData: ClearAppointment): Promise<void> {
          await this.removeClearCodeState(postData.appointmentId);

          const url = endpoints.appointment.clear.replace(':id', `${postData.appointmentId}`);

          this.apiService.post<ClearAppointment>(url, postData);
     }

     addAttachment(appointmentId: number, formData: FormData): Observable<AttachmentUploadResponse> {
          const url = endpoints.appointment.attachment.replace(':id', `${appointmentId}`);

          return this.apiService.post(url, formData);
     }

     downloadAttachment(appointmentId: number, attachment: AttachmentFile): Promise<Observable<any>> {
          const url = endpoints.appointment.downloadAttachment
               .replace(':id', `${appointmentId}`)
               .replace(':attachmentId', `${attachment.xrefId}`);

          return this.downloadFileService.download(url, attachment.filename);
     }

     async getClearCodeState(appointmentId: number): Promise<AppointmentClearCode> {
          let state = await this.storage.appointmentClearCode.getItem<AppointmentClearCode>(appointmentId);
          if (!state) {
               const appointment = await this.getLocalAppointment(appointmentId);
               if (!appointment) {
                    return;
               }

               state = new AppointmentClearCode({
                    appointmentId: appointment.id,
                    codeId: appointment.ticketCodeId,
               });
          }

          return state;
     }

     async saveClearCodeState(appointmentId: number, state: AppointmentClearCode): Promise<void> {
          await this.storage.appointmentClearCode.setItem(`${appointmentId}`, state);
     }

     async removeClearCodeState(appointmentId: number): Promise<void> {
          await this.storage.appointmentClearCode.removeItem(`${appointmentId}`);
     }

     async getLocalAppointment(appointmentId: number): Promise<Appointment> {
          return await this.storage.appointments.getItem<Appointment>(appointmentId);
     }

     async saveLocalAppointment(appointment: Appointment): Promise<void> {
          await this.storage.appointments.setItem(appointment.id, appointment);
          this.setActiveAppointment(appointment);
     }

     async getAllCharges(appointmentId: number): Promise<Charge[]> {
          const appointmentCharges = await this.getAppointmentCharges(appointmentId);
          const localCharges = await this.getLocalCharges(appointmentId);

          return [
               ...appointmentCharges,
               ...localCharges,
          ];
     }

     async getAppointmentCharges(appointmentId: number): Promise<Charge[]> {
          const appointment = await this.getLocalAppointment(appointmentId);
          const charges = appointment.additionalCharges;
          charges.sort((a, b) => (a.id - b.id));

          return charges;
     }

     async getLocalCharges(appointmentId: number): Promise<Charge[]> {
          const record = await this.getClearCodeState(appointmentId);
          const charges = record.clearCode.charges.map(charge => {
               charge.desc = charge.chargeCode.desc;
               return charge;
          });

          // Sort oldest->newest
          charges.sort((a, b) => a.timestamp < b.timestamp ? -1 : 1);

          return charges;
     }

     async removeCharge(appointmentId: number, charge: Charge): Promise<void> {
          if (charge.timestamp) {
               const record = await this.getClearCodeState(appointmentId);
               const index = record.clearCode.charges.findIndex(c => c.timestamp === charge.timestamp);

               record.clearCode.charges.splice(index, 1);

               this.saveClearCodeState(appointmentId, record);
          }

          if (charge.id) {
               // Update idb
               const appointment = await this.getLocalAppointment(appointmentId);
               const index = appointment.additionalCharges.findIndex(c => c.id === charge.id);

               appointment.additionalCharges.splice(index, 1);

               this.saveLocalAppointment(appointment);

               // Update API
               const url = endpoints.appointment.additionalCharges.delete
                    .replace(':id', `${appointmentId}`)
                    .replace(':xrefId', `${charge.id}`);

               this.apiService.delete(url, {});
          }
     }

     async addWorkforceForm(appointmentId: number, formEntry: WorkforceFormEntry): Promise<void> {
          const url = endpoints.appointment.formFieldEntries.replace(':id', `${appointmentId}`).replace(':formId', `${formEntry.id}`);
          await this.apiService.post<FieldEntry[]>(url, formEntry.entries);
          const appointment = await this.getLocalAppointment(appointmentId);
          appointment.forms.push(formEntry);
          this.saveLocalAppointment(appointment);
     }
     async updateWorkforceForm(appointmentId: number, formEntry: WorkforceFormEntry): Promise<void> {
          console.table(formEntry);
          const url = endpoints.appointment.formFieldEntries.replace(':id', `${appointmentId}`).replace(':formId', `${formEntry.id}`);
       
          await this.apiService.post<FieldEntry[]>(url, formEntry.entries);
          const appointment = await this.getLocalAppointment(appointmentId);
          const index = appointment.forms.findIndex(f => f.id === formEntry.id);
          appointment.forms[index] = formEntry;
          this.saveLocalAppointment(appointment);
     }
     async deleteWorkforceForm(appointmentId: number, formEntry: WorkforceFormEntry): Promise<void> {
          const url = endpoints.appointment.deleteFormFieldEntries.replace(':id', `${appointmentId}`).replace(':formEntryId', `${formEntry.id}`);

          console.log(url);

          await this.apiService.delete(url, {});
          const appointment = await this.getLocalAppointment(appointmentId);
          appointment.forms = appointment.forms.filter(f => f.id !== formEntry.id);
          this.saveLocalAppointment(appointment);
     }

}
